import Badge from '@/common/components/Badge';
import Button from '@/common/components/Button';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { ShowHeader } from '@/common/components/ShowHeader';
import Tabs from '@/common/components/Tabs';
import { CarFrontIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import EquipmentLogChanges from '../../components/show/changeHistory';
import { ConsumptionHistory } from '../../components/show/consumptionHistory';
import { EquipmentData } from '../../components/show/equipmentData';
import { EquipmentLastUsageDeclarationsPerCategory } from '../../components/show/lastUsageDeclarationsPerCategory';
import { FipeData } from '../../components/show/fipeData';
import useEquipment from '../../hooks/useEquipment';
import { useGetCurrentTab } from '@/common/hooks/useGetCurrentTab';

const EquipmentsShow = () => {
  const { id: equipmentId } = useParams();

  const {
    data: equipment,
    isPending: equipmentIsPending,
    error: equipmentError = {} as any,
    refetch: refetchEquipment,
    dataUpdatedAt: equipmentUpdatedAt,
  } = useEquipment({ id: equipmentId || '' });

  const disableUpdateButton = equipmentIsPending || equipmentError?.status === 403;

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipmentUpdatedAt]);

  const currentTab = useGetCurrentTab(`/equipments/${equipmentId}`);

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={equipmentError?.status}
      refetchFunction={refetchEquipment}
    >
      <div>
        <ShowHeader
          goBack
          goBackProps={{ fallback: '/equipments', searchLastRouteFromReference: `/equipments/${equipmentId}` }}
          logoUrl={equipment?.photos?.[0]?.url}
          isLoading={equipmentIsPending}
          title={(
            <div className='flex items-center gap-1.5'>
              {categoryFieldForDisplayName === 'license_plate' && (
                <CarFrontIcon strokeWidth={1.5} size={18} />
              )}

              <h2>
                {equipmentDisplayName}
              </h2>
            </div>
          )}
          badge={
            equipment?.status === 'disabled' ? (
              <Badge
                Icon={React.Fragment}
                text="Desativado"
              />
            ) : equipment?.status === 'blocked' ? (
              <Badge
                Icon={React.Fragment}
                text="Bloqueado"
              />
            ) : <></>
          }
          buttons={(
            <>
              {!currentTab && (
                <Link
                  to={disableUpdateButton ? '' : `/equipments/${equipmentId}/edit`}
                  className={disableUpdateButton ? 'cursor-default' : ''}
                >
                  <Button
                    type="button"
                    className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                    disabled={disableUpdateButton}
                    name='update'
                  >
                    Editar
                  </Button>
                </Link>
              )}
            </>
          )}
        />

        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6 flex flex-col max-h-[calc(100vh-186px)]"
          tabsList={[
            {
              label: 'Dados',
              value: '',
              body: <EquipmentData equipmentId={equipmentId} equipment={equipment} />,
            },
            {
              label: 'Histórico de Uso',
              value: 'lastUsageDeclarationsPerCategory',
              body: <EquipmentLastUsageDeclarationsPerCategory equipment={equipment} />,
              operationKlassToCheckPermission: 'Client::Equipments::LastUsageDeclarationsPerCategoryFilter',
            },
            {
              label: 'Histórico de transações',
              value: 'consumptionHistory',
              body: <ConsumptionHistory equipment={equipment} />,
            },
            {
              label: 'Histórico de mudanças',
              value: 'changeHistory',
              body: <EquipmentLogChanges equipmentId={equipmentId} />,
            },
            {
              label: 'Dados FIPE',
              value: 'fipeData',
              body: <FipeData id={equipment?.model?.id} />,
              operationKlassToCheckPermission: 'Client::EquipmentModels::LatestFipe'
            }
          ]}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default EquipmentsShow;
